/**
 * upload-pipeline.js — pure, dependency-injected core of the SUP-760 direct-upload flow.
 *
 * This file is loaded as a classic browser <script> BEFORE upload-file.js and exposes its API
 * on `globalThis.ColmanUploadPipeline`. It deliberately uses NO import/export so the exact same
 * file is also importable by Node's test runner (a side-effect import populates globalThis).
 *
 * Design:
 *  - Pure functions for every decision (validation, classification, message derivation, request
 *    building). They never touch the network, the DOM, or globals — same input, same output.
 *  - A thin imperative shell (the `*Upload`/`poll*`/`runUpload` orchestrators) performs I/O via
 *    injected dependencies (`fetch`, `FormData`, `log`). Nothing reaches for a global directly,
 *    which is what makes the whole pipeline testable without a browser.
 *  - Errors are values inside the pure layer (tagged unions: { kind: "success" | "processing" |
 *    "error", ... }); the shell translates them into thrown Errors at the boundary, and never
 *    leaks a raw network/parse error to the shopper — every failure collapses to a safe message.
 */
(function attachUploadPipeline(root) {
  "use strict";

  // 20 MiB — Shopify's staged-upload policy cap. The app proxy also refuses large multipart
  // bodies, which is the whole reason for the 3-leg handoff (SUP-760).
  const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
  const DEFAULT_MAX_STATUS_ATTEMPTS = 5;
  // Delay between /status polls. The backend caps each poll at a ~25s window, so with 5 attempts
  // and a 1s gap the file gets ~2 minutes to become READY. Injectable via deps.wait for tests.
  const DEFAULT_POLL_INTERVAL_MS = 1000;

  const ENDPOINTS = Object.freeze({
    staged: "/apps/toolbox/api/files/staged-upload",
    complete: "/apps/toolbox/api/files/complete",
    status: "/apps/toolbox/api/files/status",
  });

  const MESSAGES = Object.freeze({
    generic: "Upload failed — please try again or contact support.",
    oversize:
      "This file is too large to upload (max 20MB). Please compress it or contact support.",
    timeout:
      "Upload is taking longer than expected. Please refresh and try again, or contact support.",
  });

  // ---------------------------------------------------------------------------
  // Pure predicates & coercions
  // ---------------------------------------------------------------------------

  const isObject = (value) =>
    typeof value === "object" && value !== null && !Array.isArray(value);

  const isNonEmptyString = (value) =>
    typeof value === "string" && value.length > 0;

  const isPositiveFiniteNumber = (value) =>
    typeof value === "number" && Number.isFinite(value) && value > 0;

  const asString = (value) => (typeof value === "string" ? value : "");

  // ---------------------------------------------------------------------------
  // Pure: derive a safe, user-facing error message from a parsed response body
  // ---------------------------------------------------------------------------

  function deriveErrorMessage(data, fallback = MESSAGES.generic) {
    if (isObject(data)) {
      if (data.type === "error" && isNonEmptyString(data.message)) {
        return data.message;
      }
      // HttpError responses from the backend serialize as { error: "..." }.
      if (isNonEmptyString(data.error)) {
        return data.error;
      }
    }
    return fallback;
  }

  // ---------------------------------------------------------------------------
  // Pure: pre-flight file-size guard (errors as values)
  // ---------------------------------------------------------------------------

  function checkFileSize(size, max = MAX_FILE_SIZE_BYTES) {
    // Reject NaN / 0 / negative / non-number up front: the backend would reject them anyway
    // (schema requires an integer >= 1), so we fail fast client-side instead of after a round trip.
    if (!isPositiveFiniteNumber(size)) {
      return { ok: false, message: MESSAGES.generic };
    }
    if (size > max) {
      return { ok: false, message: MESSAGES.oversize };
    }
    return { ok: true };
  }

  // ---------------------------------------------------------------------------
  // Pure: request builders (return frozen plain data — no mutation of inputs)
  // ---------------------------------------------------------------------------

  function buildStagedUploadBody(file, fileType) {
    const source = isObject(file) ? file : {};
    return Object.freeze({
      filename: asString(source.name),
      mimeType: isNonEmptyString(source.type)
        ? source.type
        : "application/octet-stream",
      fileSize: source.size,
      fileType,
    });
  }

  function buildCompleteBody(resourceUrl, fileType, token) {
    return Object.freeze({ resourceUrl, fileType, token });
  }

  function buildStatusPath(id, token) {
    const query = new URLSearchParams({
      id: asString(id),
      token: asString(token),
    });
    return `${ENDPOINTS.status}?${query.toString()}`;
  }

  // ---------------------------------------------------------------------------
  // Pure: classify normalized responses into tagged unions
  //   response shape: { ok: boolean, status: number, data: unknown }
  // ---------------------------------------------------------------------------

  function classifyStagedTarget(response) {
    if (!isObject(response) || !response.ok) {
      return {
        kind: "error",
        message: deriveErrorMessage(response && response.data),
      };
    }
    const data = response.data;
    const target = isObject(data) ? data.target : null;
    const valid =
      isObject(data) &&
      data.type === "success" &&
      isObject(target) &&
      isNonEmptyString(target.url) &&
      isNonEmptyString(target.resourceUrl) &&
      isNonEmptyString(target.token);
    if (!valid) {
      return { kind: "error", message: deriveErrorMessage(data) };
    }
    return { kind: "success", target };
  }

  // Shared by /complete and /status — both may report success, still-processing, or failure.
  function classifyFileResult(response) {
    if (!isObject(response) || !response.ok) {
      return {
        kind: "error",
        message: deriveErrorMessage(response && response.data),
      };
    }
    const data = response.data;
    if (
      isObject(data) &&
      data.type === "processing" &&
      isNonEmptyString(data.id) &&
      isNonEmptyString(data.statusToken)
    ) {
      return { kind: "processing", id: data.id, statusToken: data.statusToken };
    }
    const image = isObject(data) ? data.image : null;
    if (
      isObject(data) &&
      data.type === "success" &&
      isObject(image) &&
      isNonEmptyString(image.url) &&
      isNonEmptyString(image.id)
    ) {
      return {
        kind: "success",
        image: Object.freeze({ url: image.url, id: image.id }),
      };
    }
    return { kind: "error", message: deriveErrorMessage(data) };
  }

  // ---------------------------------------------------------------------------
  // Shell: dependency resolution + safe I/O helpers
  // ---------------------------------------------------------------------------

  function makeDeps(overrides) {
    const source = isObject(overrides) ? overrides : {};
    const log = source.log || (root && root.console) || { error() {} };
    return {
      fetch:
        typeof source.fetch === "function"
          ? source.fetch
          : typeof (root && root.fetch) === "function"
            ? root.fetch.bind(root)
            : null,
      FormData: source.FormData || (root && root.FormData),
      wait:
        typeof source.wait === "function"
          ? source.wait
          : (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
      log,
    };
  }

  // Read + JSON-parse a fetch Response without ever throwing. Returns the normalized shape the
  // pure classifiers expect: { ok, status, data }.
  async function readResponse(response, log) {
    const ok = Boolean(response && response.ok);
    const status = (response && response.status) || 0;

    let text = "";
    try {
      if (response && typeof response.text === "function") {
        text = await response.text();
      }
    } catch {
      text = "";
    }

    let data = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = null;
      }
    }

    if (
      (!ok || (isObject(data) && data.type === "error")) &&
      log &&
      typeof log.error === "function"
    ) {
      log.error("Upload endpoint error", status, text);
    }

    return { ok, status, data };
  }

  // Single choke point for JSON endpoint calls: collapses network failures and a missing fetch
  // implementation into a safe generic error, and normalizes the response for classification.
  async function fetchJson(deps, url, init) {
    const resolved = makeDeps(deps);
    if (typeof resolved.fetch !== "function") {
      throw new Error(MESSAGES.generic);
    }
    let response;
    try {
      response = await resolved.fetch(url, init);
    } catch {
      throw new Error(MESSAGES.generic);
    }
    return readResponse(response, resolved.log);
  }

  // ---------------------------------------------------------------------------
  // Shell: the three legs + status polling + full orchestrator
  // ---------------------------------------------------------------------------

  // Leg 1 — ask the backend for a staged-upload target (JSON stays well under the proxy cap).
  async function requestStagedUpload(deps, params) {
    const resolved = makeDeps(deps);
    const args = isObject(params) ? params : {};
    const body = buildStagedUploadBody(args.file, args.fileType);
    const response = await fetchJson(resolved, ENDPOINTS.staged, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const result = classifyStagedTarget(response);
    if (result.kind !== "success") {
      throw new Error(result.message);
    }
    return result.target;
  }

  // Leg 2 — upload the bytes straight to storage (no proxy involved). Storage returns 2xx on success.
  async function uploadBytesToTarget(deps, target, file) {
    const resolved = makeDeps(deps);
    if (
      typeof resolved.fetch !== "function" ||
      typeof resolved.FormData !== "function"
    ) {
      throw new Error(MESSAGES.generic);
    }

    const stagedTarget = isObject(target) ? target : {};
    if (!isNonEmptyString(stagedTarget.url)) {
      // Never POST bytes to an undefined/relative URL; treat a malformed target as a failure.
      throw new Error(MESSAGES.generic);
    }

    const form = new resolved.FormData();
    // Storage providers require their parameters appended in order, before the file.
    const parameters = Array.isArray(stagedTarget.parameters)
      ? stagedTarget.parameters
      : [];
    for (const param of parameters) {
      if (isObject(param) && "name" in param) {
        form.append(param.name, param.value);
      }
    }
    form.append("file", file);

    let response;
    try {
      // No custom headers: let the runtime set the multipart boundary.
      response = await resolved.fetch(stagedTarget.url, {
        method: "POST",
        body: form,
      });
    } catch {
      throw new Error(MESSAGES.generic);
    }

    if (!response || !response.ok) {
      let text = "";
      try {
        text =
          response && typeof response.text === "function"
            ? await response.text()
            : "";
      } catch {
        text = "";
      }
      if (resolved.log && typeof resolved.log.error === "function") {
        resolved.log.error(
          "Staged upload POST failed",
          response && response.status,
          text,
        );
      }
      throw new Error(MESSAGES.generic);
    }
  }

  // Leg 3 — finalize + register the file. Shopify may still be processing when the proxy
  // read-timeout is hit; the backend then returns { type:"processing", id, statusToken } and we poll.
  async function completeUpload(deps, params) {
    const resolved = makeDeps(deps);
    const args = isObject(params) ? params : {};
    const body = buildCompleteBody(args.resourceUrl, args.fileType, args.token);
    const response = await fetchJson(resolved, ENDPOINTS.complete, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    const result = classifyFileResult(response);
    if (result.kind === "success") {
      return result.image;
    }
    if (result.kind === "processing") {
      return pollFileStatus(resolved, {
        id: result.id,
        token: result.statusToken,
        maxAttempts: args.maxAttempts,
        pollIntervalMs: args.pollIntervalMs,
      });
    }
    throw new Error(result.message);
  }

  // Poll /status until the file is READY. Each round the backend issues a fresh statusToken.
  async function pollFileStatus(deps, params) {
    const resolved = makeDeps(deps);
    const args = isObject(params) ? params : {};
    const maxAttempts = isPositiveFiniteNumber(args.maxAttempts)
      ? args.maxAttempts
      : DEFAULT_MAX_STATUS_ATTEMPTS;
    // 0 is a valid interval (used by tests to skip waiting), so allow any finite, non-negative number.
    const pollIntervalMs =
      typeof args.pollIntervalMs === "number" &&
      Number.isFinite(args.pollIntervalMs) &&
      args.pollIntervalMs >= 0
        ? args.pollIntervalMs
        : DEFAULT_POLL_INTERVAL_MS;

    let token = args.token;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const response = await fetchJson(
        resolved,
        buildStatusPath(args.id, token),
        {
          method: "GET",
        },
      );
      const result = classifyFileResult(response);
      if (result.kind === "success") {
        return result.image;
      }
      if (result.kind === "processing") {
        token = result.statusToken;
        // Wait before the next poll so we don't burn every attempt in a tight loop when the
        // backend responds instantly. Skip the wait after the final attempt (about to time out).
        if (attempt < maxAttempts - 1) {
          await resolved.wait(pollIntervalMs);
        }
        continue;
      }
      throw new Error(result.message);
    }
    throw new Error(MESSAGES.timeout);
  }

  // Full 3-leg orchestrator. Returns a frozen { url, id } or throws a safe Error.
  async function runUpload(deps, params) {
    const resolved = makeDeps(deps);
    const args = isObject(params) ? params : {};
    const file = args.file;

    const sizeCheck = checkFileSize(isObject(file) ? file.size : undefined);
    if (!sizeCheck.ok) {
      throw new Error(sizeCheck.message);
    }

    const target = await requestStagedUpload(resolved, {
      file,
      fileType: args.fileType,
    });
    await uploadBytesToTarget(resolved, target, file);
    const image = await completeUpload(resolved, {
      resourceUrl: target.resourceUrl,
      fileType: args.fileType,
      token: target.token,
      maxAttempts: args.maxAttempts,
      pollIntervalMs: args.pollIntervalMs,
    });

    return Object.freeze({ url: image.url, id: image.id });
  }

  const api = Object.freeze({
    MAX_FILE_SIZE_BYTES,
    DEFAULT_MAX_STATUS_ATTEMPTS,
    DEFAULT_POLL_INTERVAL_MS,
    ENDPOINTS,
    MESSAGES,
    // pure predicates / coercions
    isObject,
    isNonEmptyString,
    isPositiveFiniteNumber,
    asString,
    // pure logic
    deriveErrorMessage,
    checkFileSize,
    buildStagedUploadBody,
    buildCompleteBody,
    buildStatusPath,
    classifyStagedTarget,
    classifyFileResult,
    // shell
    makeDeps,
    readResponse,
    fetchJson,
    requestStagedUpload,
    uploadBytesToTarget,
    completeUpload,
    pollFileStatus,
    runUpload,
  });

  root.ColmanUploadPipeline = api;
})(typeof globalThis !== "undefined" ? globalThis : this);
